import React from "react";
import { Link } from "react-router-dom";
import { Avatar, Card } from "../../components/Ui";
import SourceTip, { TitleWithSource } from "../../components/SourceTip";
import { avatarSrc } from "../../hooks/useBdApi";
import { formatNumber } from "../../metrics";
import { agpWindowLabel } from "./AgpUi";

// Order of the four figures everywhere they appear.
export const RATE_KEYS = [
  { key: "tap", label: "TAPs" },
  { key: "clientMeeting", label: "Client Meetings" },
  { key: "lmv", label: "LMVs" },
  { key: "all", label: "All meetings" },
];

export const formatRate = (rate) => (rate === null || rate === undefined || !Number.isFinite(rate) ? "—" : `${Math.round(rate * 100)}%`);

function rateTitle(r, label) {
  if (!r) return label;
  return `${label}: ${formatNumber(r.taken)} held (Call Taken On this year) of ${formatNumber(r.booked)} booked (Booked On this year)`;
}

// Neutral when nothing booked; otherwise a light tone so very low rates stand out.
function rateTone(r) {
  if (!r || r.rate === null || r.rate === undefined) return "neutral";
  if (r.rate >= 0.75) return "success";
  if (r.rate >= 0.5) return "warning";
  return "danger";
}

/**
 * Four tiles: "TAPs held 145 / 177 booked · 82%" etc. for one person.
 */
export function MeetingRatesStrip({ rates, window }) {
  if (!rates) return null;
  return (
    <div className="bd-rates">
      {RATE_KEYS.map(({ key, label }) => {
        const r = rates[key];
        return (
          <div key={key} className="bd-rates__item" title={rateTitle(r, label)}>
            <div className="bd-rates__label">
              {label} held
              {key === "tap" && <SourceTip metric="agp:meetingRates" title="Booked vs held" />}
            </div>
            <div className="bd-rates__value">
              <strong className={`bd-rates__pct bd-rates__pct--${rateTone(r)}`}>{formatRate(r && r.rate)}</strong>
              <span className="bd-muted">
                {formatNumber(r ? r.taken : 0)} / {formatNumber(r ? r.booked : 0)} booked
              </span>
            </div>
          </div>
        );
      })}
      {window && <div className="bd-rates__note bd-muted">Booked On vs Call Taken On, {agpWindowLabel(window)}</div>}
    </div>
  );
}

/**
 * One row per member with the four percentages; counts in the cell tooltip.
 */
export function MeetingRatesCard({ members = [], window, search = "" }) {
  const rows = members.filter((m) => m.meetingRates);
  if (!rows.length) return null;
  return (
    <Card
      flush
      title={<TitleWithSource metric="agp:meetingRates">Booked vs held</TitleWithSource>}
      subtitle={`Meetings that happened (Call Taken On) as a share of meetings booked (Booked On), ${agpWindowLabel(window)} · hover a cell for the counts`}
    >
      <div className="bd-table-wrap">
        <table className="bd-table bd-agp-table bd-rates-table">
          <thead>
            <tr>
              <th>Member</th>
              {RATE_KEYS.map(({ key, label }) => (
                <th key={key} className="bd-rates-table__pct">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => (
              <tr key={m.sfUserId}>
                <td>
                  <Link to={`/landmark/bd/agp/member/${m.sfUserId}${search}`} className="bd-agp-matrix__member-link" title={m.member.name}>
                    <Avatar name={m.member.name} team={m.member.team} size="sm" src={avatarSrc(m.member)} />
                    <span>{m.member.name}</span>
                  </Link>
                </td>
                {RATE_KEYS.map(({ key, label }) => {
                  const r = m.meetingRates[key];
                  return (
                    <td key={key} className="bd-rates-table__pct" title={rateTitle(r, label)}>
                      <strong className={`bd-rates__pct bd-rates__pct--${rateTone(r)}`}>{formatRate(r && r.rate)}</strong>
                      <small className="bd-muted">
                        {formatNumber(r ? r.taken : 0)}/{formatNumber(r ? r.booked : 0)}
                      </small>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
